import * as Comp from "../../Common/CommonComponents";
import * as common from "../../Common/common";
import http from "../../Common/http-common";
import CssClass from "../../Styles/common.module.css";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { GetPageAuthorityFn } from "../..//Common/authority";
import ConfirmModal from '../../Components/BasicElements/ConfirmModal';

const PAGE_NAME = "Policy.js_";
const MODULE_ID = "WEB_MST_001";

const Policy = () => {
  document.title = common.c_TITLE + " - System Policy";

  const navigate = useNavigate();

  const [cookies] = useCookies([]);
  const [userId] = useState(cookies.USER_ID);
  const [allData, setAllData] = useState([]);
  const [inputError, setInputError] = useState({});
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading...");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const [actionAuthority, setActionAuthority] = useState({
    READ: "N",
    WRITE: "N",
  });

  useEffect(() => {
    let promise = GetPageAuthorityFn(userId, MODULE_ID);
    promise.then(
      (result) => {
        setActionAuthority(result);
        if (result.READ === "N") {
          navigate("/Dashboard");
        }
      },
      (error) => {
        setActionAuthority({
          READ: "N",
          WRITE: "N",
        });
        common.c_LogWebError(PAGE_NAME, "GetPageAuthorityFn", error);
        toast.error(error);
        navigate("/Dashboard");
      }
    );
  }, [userId, navigate]);

  useEffect(() => {
    if (actionAuthority.READ === "Y") {
      let functionName = "";

      try {
        functionName = "useEffect Get All System Policy";

        setLoading(true);
        setLoadingText("Getting System Policy List, please wait...");

        setAllData([]);

        const listItems = [];

        http
          .get("api/policy/GetAllPolicy?_userId=" + userId, {
            timeout: 10000000,
          })
          .then((response) => {
            if (response.data.length > 0) {
              response.data.forEach((data) => {
                listItems.push({
                  LOGIN_FAIL_COUNT: data.LOGIN_FAIL_COUNT,
                  AUTO_LOGOUT_DURATION: data.AUTO_LOGOUT_DURATION,
                  PASSWORD_REGEX: data.PASSWORD_REGEX,
                  SUMMARY_1: data.SUMMARY_1,
                  SUMMARY_2: data.SUMMARY_2,
                  LEAVE_TAKEN_BY_MTD: data.LEAVE_TAKEN_BY_MTD,
                });
              });
              setAllData(listItems[0]);
              setIsChecked(listItems[0].LEAVE_TAKEN_BY_MTD === 'Y');
            }
          })
          .catch((err) => {
            toast.error(
              "Error on getting system policy list. Please try again."
            );
            common.c_LogWebError(PAGE_NAME, functionName, err);
          })
          .finally(() => {
            setLoading(false);
            setLoadingText("Loading...");
          });
      } catch (err) {
        toast.error(
          "Error on getting system policy list. Please try again."
        );
        common.c_LogWebError(PAGE_NAME, functionName, err);
      }
    }  
  }, [reload, actionAuthority, userId]);

  //#region Save
  const SaveEditPolicy = (_data) => {
    let functionName = "";

    try {
      functionName = SaveEditPolicy.name;

      onLoadingHandler(true, "Configuring Policy , please wait...");

      const data = {
        LOGIN_FAIL_COUNT: _data.LOGIN_FAIL_COUNT,
        AUTO_LOGOUT_DURATION: _data.AUTO_LOGOUT_DURATION,
        PASSWORD_REGEX: _data.PASSWORD_REGEX,
        SUMMARY_1: _data.SUMMARY_1,
        SUMMARY_2: _data.SUMMARY_2,
        LEAVE_TAKEN_BY_MTD: isChecked ? 'Y' : 'N',
        UPDATE_ID: userId,
        FROM_SOURCE: { SOURCE: "WEB", MODULE_ID }
      };
      http
        .post("api/policy/UpdatePolicy", data, { timeout: 10000 })
        .then(() => {
          toast.success("System policy is successfully modified.");
          onReloadHandler();
		      setHasChanges(false);
        })
        .catch((err) => {
          toast.error("Failed to update system policy. Please try again.");
          common.c_LogWebError(PAGE_NAME, functionName, err);
        })
        .finally(() => {
          onLoadingHandler(false, "Loading...");
        });
    } catch (err) {
      onLoadingHandler(false, "Loading...");
      toast.error("Failed to update system policy. Please try again.");
      common.c_LogWebError(PAGE_NAME, functionName, err);
    }
  };

  const onLoadingHandler = (load, text) => {
    setLoading(load);
    setLoadingText(text);
  };

  const onReloadHandler = () => {
    setReload(!reload);
  };

  const handleConfirm = () => {
    if (hasChanges) {
      let functionName = "";

      try {
        functionName = SaveEditPolicy.name;

        if (Object.values(inputError).filter((v) => v !== "").length === 0) {
          setShowConfirmModal(true);
        }
      } catch (err) {
        common.c_LogWebError(PAGE_NAME, functionName, err);
        return false;
      }
    }
  };

  const handleConfirmModal = () => {
    SaveEditPolicy(allData);
    setShowConfirmModal(false); // Hide confirm modal
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false); // Hide confirm modal on cancel
  };

  const inputRequiredHandler = (event) => {
    let functionName = "";

    try {
      functionName = inputRequiredHandler.name;
      const name = event.target.name;
      functionName = inputRequiredHandler.name + "_" + name;
      const id = event.target.id;
      const val = event.target.value;
      if (val.length === 0) {
        setInputError((prevState) => ({
          ...prevState,
          [id]: name + " could not be empty",
        }));
      } else {
        setInputError((prevState) => ({
          ...prevState,
          [id]: "",
        }));
      }
      setAllData((prevState) => ({
        ...prevState,
        [id]: val,
      }));
    } catch (err) {
      common.c_LogWebError(PAGE_NAME, functionName, err);
    }

		setHasChanges(true);
  };
  
  const daysInputHandler = (event) => {
		const { id, value } = event.target;
		const parsedValue = parseFloat(value);

		if (isNaN(parsedValue) || parsedValue < 0 || value.trim() === "") {
			setInputError((prevState) => ({
				...prevState,
				[id]: "Value must be a non-negative number and cannot be empty.",
			}));
		} else if (parsedValue > 31) {
      setInputError((prevState) => ({
				...prevState,
				[id]: "Please select a valid date of a month.",
			}));
    } else {
      if (id === "SUMMARY_2" && parsedValue <= parseFloat(allData.SUMMARY_1 || 0)) {
        setInputError((prevState) => ({
          ...prevState,
          [id]: "2nd summary date cannot be smaller or same with 1st summary date.",
        }));
      } else {
				setInputError((prevState) => ({
					...prevState,
					[id]: "",
				}));
      }
		}

		setAllData((prevState) => ({
			...prevState,
			[id]: value,
		}));

		setHasChanges(true);
	};

  const leaveTakenByMTDHandler = (e) => {
		setIsChecked(e.target.checked);
		setHasChanges(true);
	};

  return (
    <>
      <div className={CssClass.btnWrapper}>
        {actionAuthority.WRITE === 'Y' && (
          <Comp.Button id="btnSave" type="general" onClick={handleConfirm} style={{ marginTop: '15px' }}>
            SAVE
          </Comp.Button>
        )}
      </div>
      <div className={`${CssClass.configCard} ${CssClass.configTableCard}`} style={{ margin: "10px 20px 8px" }}>
        <h3 className={CssClass.tableTitle}>Login Policy</h3>
        <div className={CssClass.flexContainer}>
          <table className="table" style={{ borderColor: "transparent"}}>
            <tbody>
              <tr>
                <td>
                  <Comp.Input
                    id="LOGIN_FAIL_COUNT"
                    label="Maximum Failed Attempts"
                    type="number"
                    name="Login Fail Attempt"
                    errorMessage={inputError.LOGIN_FAIL_COUNT}
                    value={allData !== undefined ? allData.LOGIN_FAIL_COUNT : ""}
                    disabled={actionAuthority.WRITE !== "Y"}
                    onChange={inputRequiredHandler}
                    onBlur={inputRequiredHandler}
                  />
                </td>
                <td>
                  <Comp.Input
                    id="AUTO_LOGOUT_DURATION"
                    label="Auto Logout Duration (mins)"
                    type="number"
                    name="Auto Logout Duration"
                    errorMessage={inputError.AUTO_LOGOUT_DURATION}
                    value={allData !== undefined ? allData.AUTO_LOGOUT_DURATION : ""}
                    disabled={actionAuthority.WRITE !== "Y"}
                    onChange={inputRequiredHandler}
                    onBlur={inputRequiredHandler}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className={`${CssClass.configCard} ${CssClass.configTableCard}`} style={{ margin: "10px 20px 8px" }}>
        <h3 className={CssClass.tableTitle}>Password Policy</h3>
        <div className={CssClass.flexContainer}>
          <table className="table" style={{ borderColor: "transparent"}}>
            <tbody>
              <tr>
                <td>
                  <Comp.Input
                    id="PASSWORD_REGEX"
                    label="Password Pattern"
                    type="text"
                    name="Password Regex"
                    errorMessage={inputError.PASSWORD_REGEX}
                    value={
                      allData !== undefined ? allData.PASSWORD_REGEX : ""
                    }
                    disabled={actionAuthority.WRITE !== "Y"}
                    onChange={inputRequiredHandler}
                    onBlur={inputRequiredHandler}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className={`${CssClass.configCard} ${CssClass.configTableCard}`} style={{ margin: "10px 20px 8px" }}>
        <h3 className={CssClass.tableTitle}>Email Notification Settings</h3>
        <div className={CssClass.flexContainer}>
          <table className="table" style={{ borderColor: "transparent"}}>
            <tbody>
              <tr>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <label>Summary (1st)</label>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>Every</span>
                      <input
                        id="SUMMARY_1"
                        type="number"
                        name="First Summary"
                        className="input-fields-days"
                        min="0"
                        step="1"
                        value={allData.SUMMARY_1}
                        onChange={daysInputHandler}
                        style={{ width: "35px", height: "35px", margin: "10px", padding: "5px", textAlign: "center" }}
                      />
                      <span>(date) of the month</span>
                    </div>
                  </div>
                  <div style={{ minHeight: "18px", color: "#dc3545", fontSize: "14px" }}>
                    {inputError.SUMMARY_1}
                  </div>
                </td>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <label>Summary (2nd)</label>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>Every</span>
                      <input
                        id="SUMMARY_2"
                        type="number"
                        name="Second Summary"
                        className="input-fields-days"
                        min="0"
                        step="1"
                        value={allData.SUMMARY_2}
                        onChange={daysInputHandler}
                        style={{ width: "35px", height: "35px", margin: "10px", padding: "5px", textAlign: "center" }}
                      />
                      <span>(date) of the month</span>
                    </div>
                  </div>
                  <div style={{ minHeight: "18px", color: "#dc3545", fontSize: "14px" }}>
                    {inputError.SUMMARY_2}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className={`${CssClass.configCard} ${CssClass.configTableCard}`} style={{ margin: "10px 20px 20px" }}>
        <h3 className={CssClass.tableTitle}>System Policy</h3>
        <div className={CssClass.flexContainer}>
          <table className="table" style={{ borderColor: "transparent"}}>
            <tbody>
              <tr>
                <td>
                  <div style={{ display: "flex", flexDirection: "center", gap: "12px" }}>
                    <label>Max Leave Taken By MTD Count</label>
                    <input
                      id="LEAVE_TAKEN_BY_MTD"
                      type="checkbox"
                      name="Leave Taken By MTD"
                      checked={isChecked}
                      disabled={actionAuthority.WRITE !== "Y"}
                      onChange={leaveTakenByMTDHandler}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

			<ConfirmModal
				show={showConfirmModal}
				onHide={handleConfirmModalClose}
				onConfirm={handleConfirmModal}
				prop="Confirm to save"
			>
				Are you sure you want to update system policy?
			</ConfirmModal>

      {loading && <Comp.Loading>{loadingText}</Comp.Loading>}
      <Comp.AlertPopup />
    </>
  );
};

export default Policy;
